import {
  prisma,
  type Prisma,
  Exhibitor,
  TaskTransitionType,
  InvitationStatus,
  UserRoleAssignmentEntityType,
  AvailabilitiesModel,
  User,
  FormEntityType,
  FormEntity,
  ExhibitionPrivacy,
  ContactBookGroupType,
  NetworkingModel,
  ParticipantGroupType,
  ParticipantType,
  NetworkingType,
} from "client";
import {
  createParticipantFormTemplate,
  transformSurveyResult,
} from "../formTemplates";
import { adoptUnscopedUser } from "services/user/scopedUser";

function participantGroupTranslationName(
  group: {
    id: string;
    translations?: {
      name?: string | null;
      language?: { code?: string | null } | null;
    }[];
  },
  lang?: string
) {
  return (
    group.translations?.find((item) => item.language?.code === lang)?.name ??
    group.translations?.[0]?.name
  );
}

const enum Status {
  RESERVED = "Reserved",
  PENDING_APPROVAL = "Pending Approval",
  WAITING_FOR_PAYMENT = "Waiting For Payment",
}
import jwt from "jsonwebtoken";
import { createDefaultAvailabilities } from "../availability";
import { VALIDATION_RULES } from "@sajilni/config";
import {
  DEFAULT_QUESTIONS,
  toStorableFormQuestion,
} from "@sajilni/utils/formTemplate";
import { isMongoObjectId } from "@sajilni/utils/ticketQr";

import { getLanguages } from "services/language";
import { getContactBookGroupByExhibitorId } from "services/contactBookGroup";
import { createDefaultNetworkingSettings } from "services/networkingSettings";
import { invalidateEventPageCacheForEvent } from "../event/eventPageCache";
import {
  isUserSponsorInEvent,
  isSponsorTeamMember,
  getSponsorByEventSlugAndUserId,
} from "services/sponsor";
import { USER_TYPES } from "@sajilni/config/constants";
import { getWhiteLabelByEventId } from "services/white-label";
import {
  assertNotOwnerTeamMember,
  updatePortalTeamMember,
  cleanupCompanyTeamUsers,
  syncCompanyAdminUserAndProfile,
  getCompanyAdminEmailConflict,
} from "services/team";
// READ
export const getExhibitors = async (): Promise<Exhibitor[]> => {
  return await prisma.exhibitor.findMany();
};

export const isUserParticipantInEvent = async (
  email: string,
  eventSlug: string,
  entityType: ParticipantGroupType | USER_TYPES
) => {
  if (
    entityType === ParticipantGroupType.EXHIBITORS ||
    entityType === USER_TYPES.EXHIBITOR
  ) {
    return await isUserExhibitorInEvent(email, eventSlug);
  }
  if (
    entityType === ParticipantGroupType.SPONSORS ||
    entityType === USER_TYPES.SPONSOR
  ) {
    return await isUserSponsorInEvent(email, eventSlug);
  }
  if (
    entityType === USER_TYPES.VISITOR ||
    entityType === ParticipantGroupType.VISITORS
  ) {
    return await isUserVisitor(email, eventSlug);
  }
  return false;
};

export const isUserVisitor = async (email: string, eventSlug: string) => {
  const userVisitor = await prisma.user.findFirst({
    where: {
      email,
      events: {
        some: {
          slug: eventSlug,
        },
      },
    },
  });
  return userVisitor ?? false;
};

/** Indexed: Exhibitor @@index([eventId, teamIds]). */
export const isUserOnExhibitorTeam = async (
  userId: string,
  eventId: string
) => {
  const row = await prisma.exhibitor.findFirst({
    where: { eventId, teamIds: { has: userId } },
    select: { id: true },
  });
  return !!row;
};

/**
 * Membership via email + event slug.
 * Resolves User ids + Event.id first, then uses @@index([eventId, teamIds]).
 */
export const isUserExhibitorInEvent = async (
  email: string,
  eventSlug: string
) => {
  const normalizedEmail = email.toLocaleLowerCase();
  const [users, event] = await Promise.all([
    prisma.user.findMany({
      where: { email: normalizedEmail },
      select: { id: true },
    }),
    prisma.event.findUnique({
      where: { slug: eventSlug },
      select: { id: true },
    }),
  ]);
  if (!event || users.length === 0) return false;

  const row = await prisma.exhibitor.findFirst({
    where: {
      eventId: event.id,
      teamIds: { hasSome: users.map((u) => u.id) },
    },
    select: { id: true },
  });
  return !!row;
};

export const getExhibitorByUserId = async (id: string, eventSlug: string) => {
  const userExhibitor = await prisma.exhibitor.findFirst({
    where: {
      team: {
        some: {
          id,
        },
      },
      event: {
        slug: eventSlug,
      },
    },
    include: {
      team: {
        where: {
          id,
        },
        include: {
          profiles: {
            where: {
              event: { slug: eventSlug },
            },
          },
        },
      },
    },
  });
  if (
    userExhibitor &&
    userExhibitor.team[0]?.profiles[0]?.exhibitorPermissions?.eventDayBadge
  ) {
    return userExhibitor.team[0] ?? false;
  }
  return userExhibitor ?? false;
};

export const getExhibitorByEmail = async (email: string, slug: string) => {
  const userExhibitor = await prisma.exhibitor.findFirst({
    where: {
      email,
      event: {
        slug,
      },
    },
  });
  return userExhibitor ?? false;
};

export const getExhibitorById = async (
  id: string,
  lang?: string,
  options?: { includeParticipantGroup?: boolean; includeTranslations?: boolean }
) => {
  try {
    const exhibitor = await prisma.exhibitor.findUnique({
      where: { id },
      include: {
        ...(options?.includeParticipantGroup
          ? {
              participantGroup: {
                include: {
                  translations: {
                    include: {
                      language: true,
                    },
                  },
                },
              },
            }
          : {}),
        ...(options?.includeTranslations ? { translations: true } : {}),
        team: {
          include: {
            profiles: true,
          },
        },
      },
    });
    if (!exhibitor) return null;
    const groupName = exhibitor.participantGroup
      ? participantGroupTranslationName(exhibitor.participantGroup, lang)
      : undefined;

    return {
      ...exhibitor,
      ...(exhibitor.participantGroup !== undefined
        ? {
            participantGroup: exhibitor.participantGroup
              ? { ...exhibitor.participantGroup, name: groupName }
              : null,
          }
        : {}),
      contactName: exhibitor?.team
        ?.find((team) => team.email === exhibitor?.email)
        ?.profiles?.find((profile) => profile.eventId === exhibitor?.eventId)
        ?.name,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getParticipantByEventSlugAndUserId = async (
  slug: string,
  userId: string,
  eventId: string,
  type: ParticipantGroupType,
  lang?: string,
  includeTasks = true
) => {
  if (type === ParticipantGroupType.EXHIBITORS) {
    return await getExhibitorByEventSlugAndUserId(
      slug,
      userId,
      eventId,
      lang,
      includeTasks
    );
  }
  if (type === ParticipantGroupType.SPONSORS) {
    return await getSponsorByEventSlugAndUserId(
      slug,
      userId,
      eventId,
      lang,
      includeTasks
    );
  }
  return null;
};

export const getExhibitorByEventSlugAndUserId = async (
  slug: string,
  userId: string,
  eventId: string,
  lang?: string,
  includeTasks = true
) => {
  // Profile.eventId is @db.ObjectId — never pass cuid slug there (TasksTable refresh).
  // ObjectId → indexed eventId; else Event.slug (@unique) via relation.
  const profileEventWhere = isMongoObjectId(eventId)
    ? { eventId }
    : { event: { slug } };

  const res = await prisma.exhibitor.findFirst({
    where: {
      event: {
        slug,
      },
      OR: [{ id: userId }, { team: { some: { id: userId } } }],
    },
    include: {
      translations: {
        include: {
          language: true,
        },
      },
      team: {
        include: {
          profiles: {
            where: profileEventWhere,
            orderBy: {
              createdAt: "desc",
            },
          },
        },
      },
      participantGroup: {
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      },
      // Public networking profiles don't need nested task/form trees.
      ...(includeTasks
        ? {
            taskEntities: {
              where: {
                task: {
                  OR: [
                    // Show all non-hidden tasks
                    {
                      hide: false,
                    },
                    // Show hidden tasks if due date has passed
                    {
                      AND: [
                        {
                          hide: true,
                          dueDate: {
                            lt: new Date(),
                          },
                        },
                      ],
                    },
                  ],
                },
              },
              include: {
                taskTransitions: true,
                formResults: {
                  include: {
                    formResult: true,
                  },
                },
                task: {
                  include: {
                    formEntity: {
                      include: {
                        formTemplate: true,
                      },
                    },
                    taskTranslations: {
                      where: {
                        language: {
                          code: lang,
                        },
                      },
                    },
                  },
                },
              },
            },
          }
        : {}),
      networking: true,
    },
  });
  if (!res) return null;

  const langData =
    res.translations?.find((item) => item.language.code === lang) ??
    res.translations?.[0];
  const groupName =
    res.participantGroup?.translations?.find(
      (item) => item.language.code === lang
    )?.name ?? res.participantGroup?.translations?.[0]?.name;

  return {
    ...res,
    name: langData?.name,
    bio: langData?.bio,
    tags: langData?.tags,
    participantGroup: res.participantGroup
      ? { ...res.participantGroup, name: groupName }
      : null,
    team: [
      ...new Map(
        (res.team ?? []).map((member) => [member.id, member])
      ).values(),
    ].map((team) => {
      const profile = team?.profiles?.find((p) => p.eventId === eventId);
      return {
        ...team,
        ...profile,
        id: team?.id,
        name: profile?.name || team?.name,
        // Prefer person photo; never let null profile.image wipe user.image;
        // fall back to company logo so Team tab matches the profile header.
        image: profile?.image || team?.image || res.logo || null,
      };
    }),
    taskEntities: (() => {
      if (
        !includeTasks ||
        !("taskEntities" in res) ||
        !Array.isArray(res.taskEntities)
      ) {
        return [];
      }
      type NestedTaskEntity = {
        task?: {
          dueDate: Date;
          disableAfterDue?: boolean | null;
          formEntity?: {
            formTemplate?: { json?: unknown } | null;
          } | null;
        } | null;
        formResults?: Array<{
          formResult?: {
            surveyResult?: unknown;
          } | null;
        }>;
      };
      return (res.taskEntities as NestedTaskEntity[]).map((taskEntity) => ({
        ...taskEntity,
        stopAcceptingSubmissions:
          !!taskEntity.task &&
          taskEntity.task.dueDate < new Date() &&
          taskEntity.task.disableAfterDue === true,
        formResult: taskEntity.formResults?.[0]?.formResult
          ? {
              ...taskEntity.formResults[0].formResult,
              surveyResult: transformSurveyResult(
                taskEntity.task?.formEntity?.formTemplate?.json,
                taskEntity.formResults[0].formResult?.surveyResult
              ),
            }
          : null,
      }));
    })(),
  };
};

export const getExhibitorByEventAndUserId = async (
  id: string,
  userId: string,
  eventId: string,
  lang?: string
) => {
  const res = await prisma.exhibitor.findFirst({
    where: {
      event: {
        id,
      },
      team: {
        some: {
          id: userId,
        },
      },
    },
    include: {
      translations: {
        include: {
          language: true,
        },
      },
      team: {
        include: {
          profiles: {
            where: {
              eventId: eventId,
            },
            orderBy: {
              createdAt: "desc",
            },
          },
        },
      },
      participantGroup: {
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      },
      taskEntities: {
        where: {
          task: {
            OR: [
              // Show all non-hidden tasks
              {
                hide: false,
              },
              // Show hidden tasks if due date has passed
              {
                AND: [
                  {
                    hide: true,
                    dueDate: {
                      lt: new Date(),
                    },
                  },
                ],
              },
            ],
          },
        },
        include: {
          taskTransitions: true,
          formResults: {
            include: {
              formResult: true,
            },
          },
          task: {
            include: {
              formEntity: {
                include: {
                  formTemplate: true,
                },
              },
              taskTranslations: {
                where: {
                  language: {
                    code: lang,
                  },
                },
              },
            },
          },
        },
      },
    },
  });
  const langData =
    res?.translations?.find((item) => item.language.code === lang) ??
    res?.translations?.[0];
  const groupName =
    res?.participantGroup?.translations?.find(
      (item: any) => item.language?.code === lang
    )?.name ?? res?.participantGroup?.translations?.[0]?.name;

  return {
    ...res,
    name: langData?.name,
    bio: langData?.bio ?? langData?.description,
    description: langData?.description ?? langData?.bio,
    tags: langData?.tags,
    participantGroup: res?.participantGroup
      ? { ...res.participantGroup, name: groupName }
      : null,
    team: res?.team?.map((team) => ({
      ...team,
      ...team?.profiles?.find((profile) => profile.eventId === eventId),
      id: team?.id,
    })),
    taskEntities:
      res?.taskEntities?.map((taskEntity) => ({
        ...taskEntity,
        stopAcceptingSubmissions:
          taskEntity?.task?.dueDate < new Date() &&
          taskEntity?.task?.disableAfterDue === true,
        formResult: taskEntity?.formResults?.[0]?.formResult
          ? {
              ...taskEntity?.formResults[0]?.formResult,
              surveyResult: transformSurveyResult(
                taskEntity?.task?.formEntity?.formTemplate?.json,
                taskEntity?.formResults[0]?.formResult?.surveyResult
              ),
            }
          : null,
      })) || [],
  };
};

export const getEventExhibitorsWithNoInvitation = async (eventId: string) => {
  const exhibitors = await prisma.exhibitor.findMany({
    where: {
      eventId,
      teamIds: { isEmpty: false },
      exhibitorInvitations: {
        none: {},
      },
    },
    include: {
      team: {
        include: {
          profiles: true,
        },
      },
    },
  });
  return exhibitors.map((exhibitor) => ({
    ...exhibitor,
    contactName: exhibitor.team
      ?.find((team) => team.email === exhibitor.email)
      ?.profiles?.find((profile) => profile.eventId === exhibitor.eventId)
      ?.name,
  }));
};

export const getExhibitorsByEventId = async (
  eventId: string,
  lang?: string,
  options?: {
    includeParticipantGroup?: boolean;
  }
) => {
  const exhibitor = await prisma.exhibitor.findMany({
    where: {
      eventId,
    },
    include: {
      ...(options?.includeParticipantGroup
        ? {
            participantGroup: {
              include: {
                translations: {
                  include: {
                    language: true,
                  },
                },
              },
            },
          }
        : {}),
      taskEntities: {
        include: {
          taskTransitions: true,
        },
      },
      exhibitorInvitations: true,
      team: {
        include: {
          profiles: {
            where: {
              eventId: eventId,
            },
          },
        },
      },
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
      entities: true,
      // TODO: This is done for Backward compatibility with the old order items
      orderItems: {
        include: {
          order: {
            include: {
              payments: true,
            },
          },
        },
      },
    },
  });

  // TODO: This is done for Backward compatibility with the old order items
  const exhibitorOrders =
    (exhibitor as any).orderItems?.reduce((acc: any[], orderItem: any) => {
      const existingOrder = acc.find(
        (order) => order.id === orderItem.order.id
      );
      if (existingOrder) {
        existingOrder.orderItems.push(orderItem);
      } else {
        acc.push({
          ...orderItem.order,
          orderItems: [orderItem],
        });
      }
      return acc;
    }, []) || [];

  return exhibitor.map((exhibitor: any) => {
    const groupName = exhibitor.participantGroup
      ? participantGroupTranslationName(exhibitor.participantGroup, lang)
      : undefined;

    return {
      ...exhibitor,
      orders: exhibitorOrders,
      name: exhibitor.translations?.[0]?.name,
      bio: exhibitor.translations?.[0]?.bio,
      ...(exhibitor.participantGroup !== undefined
        ? {
            participantGroup: exhibitor.participantGroup
              ? { ...exhibitor.participantGroup, name: groupName }
              : null,
          }
        : {}),
      contactName: exhibitor.team?.profiles?.[0]?.name,
      contactJobTitle: exhibitor.team?.profiles?.[0]?.jobTitle,
      contactPhone: exhibitor.team?.profiles?.[0]?.phoneNumber,
      completedTasksPercentage: {
        total: exhibitor.taskEntities.length,
        completed: exhibitor.taskEntities.filter(
          (task: any) =>
            task.taskTransitions[task.taskTransitions.length - 1]?.toState ===
              TaskTransitionType.COMPLETED ||
            task.taskTransitions[task.taskTransitions.length - 1]?.toState ===
              TaskTransitionType.SUBMITTED
        ).length,
      },
    };
  });
};

export const getExhibitorsInvitationByEventId = async (eventId: string) => {
  const totalInvitation = await prisma.participantInvitation.count({
    where: {
      exhibitor: {
        eventId,
      },
      entityType: ParticipantType.EXHIBITOR,
    },
  });
  const totalAccepted = await prisma.participantInvitation.count({
    where: {
      exhibitor: {
        eventId,
      },
      entityType: ParticipantType.EXHIBITOR,
      status: InvitationStatus.ACCEPTED,
    },
  });
  return {
    totalInvitation,
    totalAccepted,
  };
};

export const getExhibitorsByEventIdDatatable = async (
  eventId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  searchByName?: string,
  lang?: string,
  orderBy?: {
    [key: string]: "asc" | "desc";
  }
) => {
  try {
    // Calculate offset for pagination
    const skip = (pageNumber - 1) * pageSize;
    const escapeRegex = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes regex special characters

    const searchParam = searchByName ? escapeRegex(searchByName) : "";
    const filter = searchParam
      ? {
          OR: [
            { email: { contains: searchParam, mode: "insensitive" } },
            { boothName: { contains: searchParam, mode: "insensitive" } },
            {
              translations: {
                some: {
                  OR: [
                    { name: { contains: searchParam, mode: "insensitive" } },
                    { bio: { contains: searchParam, mode: "insensitive" } },
                  ],
                },
              },
            },
            {
              team: {
                some: {
                  profiles: {
                    some: {
                      OR: [
                        {
                          name: { contains: searchParam, mode: "insensitive" },
                        },
                        {
                          phoneNumber: {
                            contains: searchParam,
                            mode: "insensitive",
                          },
                        },
                      ],
                    },
                  },
                },
              },
            },
          ],
        }
      : {};
    // Fetch exhibitors from the database
    const exhibitors = (await prisma.exhibitor.findMany({
      // @ts-ignore
      where: {
        teamIds: { isEmpty: false },
        eventId,
        ...filter,
      },
      include: {
        participantGroup: {
          include: {
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        team: {
          include: {
            profiles: {
              where: {
                eventId: eventId,
              },
            },
          },
        },
        taskEntities: {
          include: {
            formResults: {
              include: {
                formResult: true,
              },
            },
            taskTransitions: true,
            task: {
              include: {
                formEntity: {
                  include: {
                    formTemplate: true,
                  },
                },
                taskTranslations: {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                },
              },
            },
          },
        },
        exhibitorInvitations: true,
        translations: lang
          ? {
              where: {
                language: {
                  code: lang,
                },
              },
            }
          : true,
        entities: true,
      },
      skip,
      take: pageSize,
      orderBy: orderBy || {
        createdAt: "desc",
        // Order by creation date or any other field
      },
    })) as any;
    // Count total exhibitors for the given eventId and search filter
    const totalExhibitors = await prisma.exhibitor.count({
      // @ts-ignore
      where: {
        teamIds: { isEmpty: false },
        eventId,
        ...filter,
      },
    });
    // Return the data table with pagination info
    return {
      response: exhibitors.map((exhibitor: any) => {
        const groupName =
          exhibitor.participantGroup?.translations?.find(
            (item: any) => item.language?.code === lang
          )?.name ?? exhibitor.participantGroup?.translations?.[0]?.name;

        return {
          ...exhibitor,
          name: exhibitor.translations?.[0]?.name,
          bio: exhibitor.translations?.[0]?.bio,
          participantGroup: exhibitor.participantGroup
            ? { ...exhibitor.participantGroup, name: groupName }
            : null,
          contactName: exhibitor.team?.find(
            (team: any) => team.email === exhibitor.email
          )?.profiles?.[0]?.name,
          contactJobTitle: exhibitor.team?.find(
            (team: any) => team.email === exhibitor.email
          )?.profiles?.[0]?.jobTitle,
          contactPhone: exhibitor.team?.find(
            (team: any) => team.email === exhibitor.email
          )?.profiles?.[0]?.phoneNumber,
          completedTasksPercentage: {
            total: exhibitor.taskEntities.length,
            completed: exhibitor.taskEntities.filter(
              (task: any) =>
                task.taskTransitions[task.taskTransitions.length - 1]
                  ?.toState === TaskTransitionType.COMPLETED ||
                task.taskTransitions[task.taskTransitions.length - 1]
                  ?.toState === TaskTransitionType.SUBMITTED
            ).length,
          },
          taskEntities: exhibitor.taskEntities.map((task: any) => {
            return {
              ...task,
              timeSubmitted:
                task.taskTransitions[task.taskTransitions.length - 1]
                  ?.createdAt || null,
              formResult: task.formResults?.[0]?.formResult
                ? {
                    ...task.formResults?.[0]?.formResult,
                    surveyResult: transformSurveyResult(
                      task.task.formEntity.formTemplate.json,
                      task.formResults?.[0]?.formResult.surveyResult
                    ),
                  }
                : null,
              task: {
                ...task.task,
                name: task.task.taskTranslations?.[0]?.name,
              },
            };
          }),
          hasUploadedFiles: exhibitor.taskEntities.some(
            (task: any) =>
              task.uploadedFiles.length > 0 || task.formResults?.[0]?.formResult
          ),
        };
      }),

      pagination: {
        pageNumber,
        pageSize,
        total: totalExhibitors,
        totalPages: Math.ceil(totalExhibitors / pageSize),
        hasNextPage: totalExhibitors > pageNumber * pageSize,
      },
    };
  } catch (error) {
    console.log(error);
    return error;
  }
};

export const getExhibitorTasks = async (
  entityID: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  searchByName?: string
) => {
  const skip = (pageNumber - 1) * pageSize;
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes regex special characters

  const searchParam = searchByName ? escapeRegex(searchByName) : "";

  const filter: any = searchParam
    ? {
        OR: [
          {
            task: {
              taskTranslations: {
                some: {
                  OR: [
                    { name: { contains: searchParam, mode: "insensitive" } },
                  ],
                },
              },
            },
          },
        ],
      }
    : {};

  try {
    const tasks = await prisma.taskEntity.findMany({
      where: {
        entityID: entityID,
        ...filter,
      },
      include: {
        task: {
          include: {
            formEntity: {
              include: {
                formTemplate: true,
              },
            },
            taskTranslations: true,
          },
        },
        taskTransitions: true,
        formResults: {
          include: {
            formResult: true,
          },
        },
      },
      skip,
      take: pageSize,
      orderBy: {
        createdAt: "desc",
      },
    });
    const totalTasks = await prisma.taskEntity.count({
      where: {
        entityID,
        ...filter,
      },
    });
    return {
      data: tasks.map((task: any) => ({
        ...task,
        name: task.task.taskTranslations?.[0]?.name,
        timeSubmitted:
          task.taskTransitions[task.taskTransitions.length - 1]?.createdAt ||
          null,
        formResult: task.formResults?.[0]?.formResult
          ? {
              ...task.formResults[0].formResult,
              surveyResult: transformSurveyResult(
                task.task.formEntity.formTemplate.json,
                task.formResults?.[0]?.formResult.surveyResult
              ),
            }
          : null,
      })),
      pagination: {
        pageNumber,
        pageSize,
        total: totalTasks,
        totalPages: Math.ceil(totalTasks / pageSize),
        hasNextPage: totalTasks > pageNumber * pageSize,
      },
    };
  } catch (error) {
    console.log(error);
    return error;
  }
};

type ExhibitorCreateData = Prisma.ExhibitorCreateInput & {
  phone: string;
  contactName: string;
  contactJobTitle?: string;
  bio?: string | null;
  company?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  sector?: string | null;
  birthday?: Date | string | null;
  gender?: string | null;
  phoneNumber?: string | null;
  qrReferenceNumber?: string | null;
  age?: number | null;
  jobTitle?: string | null;
  name?: string | null;
};

export const createExhibitor = async (
  data: ExhibitorCreateData,
  eventId: string,
  translations: Prisma.ExhibitorTranslationsCreateManyExhibitorInput[],
  whiteLabelId: string,
  session?: User,
  participantGroupId?: string,
  orderItemId?: string,
  participantImportId?: string
) => {
  try {
    const languages = await getLanguages();
    const jsonData = {
      title: "",
      logoPosition: "",
      pages: [
        {
          name: "page1",
          elements: Object.keys(data).map((fieldName) => {
            const existingQuestion = DEFAULT_QUESTIONS.find(
              (q) => q?.name === fieldName
            );
            const element = existingQuestion ?? {
              type: "text",
              name: fieldName,
              title: fieldName,
            };
            return toStorableFormQuestion(
              element as Record<string, unknown>
            ) as typeof element;
          }),
        },
      ],
    };
    const exhibitorSuperAdminRole = await prisma.role.findFirst({
      where: {
        name: "EXHIBITOR_SUPER_ADMIN",
      },
    });
    // check if exhibitor name is exist in the same event
    const exhibitorName = translations?.[0]?.name?.trim() ?? "";
    const exhibitorNameExist = exhibitorName
      ? await prisma.exhibitor.findFirst({
          where: {
            eventId: eventId,
            translations: {
              some: {
                name: {
                  equals: exhibitorName,
                  mode: "insensitive",
                },
              },
            },
          },
        })
      : null;
    if (exhibitorNameExist) {
      return {
        error: "Exhibitor name is already exist in the same event",
      };
    }

    // check if exhibitor email is exist in the same event
    const exhibitorEmailExist = await prisma.exhibitor.findFirst({
      where: {
        email: data.email,
        eventId: eventId,
      },
    });
    if (exhibitorEmailExist) {
      return {
        error: "Exhibitor email is already exist in the same event",
      };
    }

    const contactBookGroup = await prisma.contactBookGroup.create({
      data: {
        type: ContactBookGroupType.EXHIBITION,
        name: `${ContactBookGroupType.EXHIBITION} ${
          translations?.[0]?.name ?? ""
        }`,
        fields: {
          create: {
            type: FormEntityType.CONTACT_BOOK_GROUP,
            formTemplate: {
              create: {
                translations: {
                  create: languages.map((lang) => ({
                    name: `${
                      translations.find((t) => t.languageId === lang.id)
                        ?.name ?? translations[0]?.name
                    } Form`,
                    language: { connect: { id: lang.id } },
                  })),
                },
                whiteLabelId,
                // createdById is optional — session.id may be auth sub, not a User row.
                json: jsonData as Prisma.InputJsonValue,
                text: JSON.stringify(jsonData),
                numberOfQuestions: Object.keys(jsonData.pages[0].elements)
                  .length,
              },
            },
          },
        },
        event: { connect: { id: eventId } },
        participantImportId,
      },
    });

    const {
      contactName,
      phone,
      contactJobTitle,
      bio,
      company,
      city,
      state,
      country,
      sector,
      birthday,
      gender,
      phoneNumber,
      qrReferenceNumber,
      age,
      jobTitle,
      name,

      ...rest
    } = data;
    // Contact person only — never fall back to company `translations.name`.
    const resolvedContactName =
      [contactName, translations?.[0]?.contactName]
        .find((value) => typeof value === "string" && value.trim())
        ?.trim() || "";
    const normalizedEmail = data.email?.toLocaleLowerCase() ?? "";
    const response = await prisma.exhibitor.create({
      data: {
        ...rest,
        email: normalizedEmail || undefined,
        phone,
        event: {
          connect: {
            id: eventId,
          },
        },
        privacy: ExhibitionPrivacy.BY_REQUEST,
        participantImportId,
        participantGroup: participantGroupId
          ? {
              connect: {
                id: participantGroupId,
              },
            }
          : undefined,
        contactBookGroups: { connect: { id: contactBookGroup.id } },
        orderItems: orderItemId ? { connect: { id: orderItemId } } : undefined,
        team: {
          connectOrCreate: {
            where: {
              email_accessibleWhiteLabelId: {
                email: normalizedEmail,
                accessibleWhiteLabelId: whiteLabelId ?? "",
              },
            },
            create: {
              email: normalizedEmail,
              phoneNumber: phone,
              name: resolvedContactName || undefined,
              accessibleWhiteLabelId: whiteLabelId ?? undefined,
              participantImportId,
              profiles: {
                create: {
                  email: normalizedEmail,
                  phoneNumber: phone,
                  name: resolvedContactName || undefined,
                  qrReferenceNumber,
                  jobTitle: contactJobTitle,
                  company: data.company,
                  country: data.country,
                  sector,
                  city: data.city,
                  state: data.state,
                  birthday: data.birthday,
                  gender: data.gender,
                  bio: data.bio,
                  eventId: eventId,
                  age: age,
                  contactBookGroupId: contactBookGroup.id,
                  participantImportId,
                },
              },
              // TODO: handle with the new roles structure
              // roles: [UserRole.EXHIBITOR_ADMIN, UserRole.MEMBER],
              userRoleAssignments: {
                create: {
                  entityType: UserRoleAssignmentEntityType.EXHIBITOR,
                  roleId: exhibitorSuperAdminRole?.id,
                },
              },
            },
          },
        },
        translations: {
          createMany: {
            data: translations.map((translation) => ({
              languageId: translation.languageId,
              name: translation.name,
              description: translation.description,
              contactName: translation.contactName,
              bio: translation.bio,
              tags: translation.tags,
              adminNotes: translation.adminNotes,
            })),
          },
        },
      },
      include: {
        team: true,
      },
    });

    if (!whiteLabelId) {
      throw new Error("White label is required to create a user");
    }
    await adoptUnscopedUser(normalizedEmail, whiteLabelId);
    const user = await prisma.user.upsert({
      where: {
        email_accessibleWhiteLabelId: {
          email: normalizedEmail,
          accessibleWhiteLabelId: whiteLabelId,
        },
      },
      update: resolvedContactName ? { name: resolvedContactName } : {},
      create: {
        email: normalizedEmail,
        name: resolvedContactName || undefined,
        accessibleWhiteLabelId: whiteLabelId,
        participantImportId,
        profiles: {
          create: {
            email: normalizedEmail,
            name: resolvedContactName || undefined,
            qrReferenceNumber,
            eventId,
            jobTitle: contactJobTitle,
            company: data.company,
            country: data.country,
            sector,
            city: data.city,
            state: data.state,
            birthday: data.birthday,
            gender: data.gender,
            age: age,
            bio: data.bio,
            contactBookGroupId: contactBookGroup.id,
            participantImportId,
          },
        },
      },
    });
    // Upsert update path only touches User — keep this event's Profile aligned.
    const profileSync = {
      ...(resolvedContactName ? { name: resolvedContactName } : {}),
      ...(normalizedEmail ? { email: normalizedEmail } : {}),
      ...(phone ? { phoneNumber: phone } : {}),
      ...(qrReferenceNumber ? { qrReferenceNumber } : {}),
      ...(contactJobTitle ? { jobTitle: contactJobTitle } : {}),
      ...(data.company ? { company: data.company } : {}),
      ...(data.country ? { country: data.country } : {}),
      ...(sector ? { sector } : {}),
      ...(data.city ? { city: data.city } : {}),
      ...(data.state ? { state: data.state } : {}),
      ...(data.bio ? { bio: data.bio } : {}),
      ...(data.gender ? { gender: data.gender } : {}),
      ...(age != null ? { age } : {}),
      ...(data.birthday ? { birthday: data.birthday } : {}),
    };
    if (Object.keys(profileSync).length) {
      await prisma.profile.updateMany({
        where: { userId: user.id, eventId },
        data: profileSync,
      });
    }
    await prisma.assignmentModel.upsert({
      where: {
        userId_entityType_entityId: {
          userId: user.id,
          entityType: UserRoleAssignmentEntityType.EXHIBITOR,
          entityId: response.id,
        },
      },
      create: {
        userId: user.id,
        entityType: UserRoleAssignmentEntityType.EXHIBITOR,
        roleId: exhibitorSuperAdminRole?.id,
        entityId: response.id,
      },
      update: {
        roleId: exhibitorSuperAdminRole?.id,
      },
    });

    // Create invitation immediately — later form/networking setup can fail and
    // leave the exhibitor without a magic-link token if this runs at the end.
    const invitationEmail =
      normalizedEmail || response.email?.toLocaleLowerCase() || "";
    const token = invitationEmail
      ? jwt.sign(
          {
            entityId: response.id,
            email: invitationEmail,
            entityType: ParticipantType.EXHIBITOR,
          },
          process.env.SECRET_HASH_PASSWORD as string
        )
      : undefined;
    if (token && invitationEmail) {
      await prisma.participantInvitation.upsert({
        where: {
          entityId_entityType_email: {
            entityId: response.id,
            entityType: ParticipantType.EXHIBITOR,
            email: invitationEmail,
          },
        },
        update: {
          token,
          userId: user.id,
          participantImportId,
        },
        create: {
          email: invitationEmail,
          status: InvitationStatus.PENDING,
          entityType: ParticipantType.EXHIBITOR,
          entityId: response.id,
          token,
          isOwner: true,
          userId: user.id,
          participantImportId,
        },
      });
    }

    // Prefer a real User row for FormTemplate.createdBy (session.id can be auth sub).
    const createdById = user.id;

    //defaults for exhibitor company
    await createDefaultAvailabilities({
      entityId: response.id,
      entityModel: AvailabilitiesModel.EXHIBITOR,
      eventId: eventId,
    });
    await createDefaultNetworkingSettings(
      eventId,
      response.id,
      NetworkingModel.EXHIBITOR,
      user,
      participantGroupId
    );
    const FormTranslations = translations?.map((t) => ({
      name: t?.name || "",
      languageId: t?.languageId,
    }));

    try {
      await createParticipantFormTemplate(
        {
          whiteLabelId: whiteLabelId,
          eventId: eventId,
          createdById,
        },
        FormTranslations as any,
        {
          json: {
            logoPosition: "right",
            pages: [
              {
                name: "page1",
                elements: [
                  {
                    type: "dropdown",
                    name: "question1",
                    title: "Meeting Location",
                    choices: [
                      {
                        value: "Item 1",
                        text: "Coffee Area",
                      },
                      {
                        value: "Item 2",
                        text: "Booth",
                      },
                      {
                        value: "Item 3",
                        text: "Networking Area",
                      },
                      {
                        value: "Item 4",
                        text: "Other",
                      },
                    ],
                  },
                  {
                    type: "text",
                    name: "question4",
                    visibleIf: "{question1} = 'Item 4'",
                    title: "Custom Location",
                  },
                  {
                    type: "dropdown",
                    name: "question2",
                    title: "Reason",
                    choices: [
                      {
                        value: "Item 1",
                        text: "Need a mentoring consultation",
                      },
                      {
                        value: "Item 2",
                        text: "Chat and idea exchange",
                      },
                    ],
                  },
                  {
                    type: "comment",
                    name: "question3",
                    title: "Description",
                  },
                ],
              },
            ],
          },
          numberOfQuestions: 4,
        } as any,
        {
          entityID: response?.id,
          type: FormEntityType.MEETING,
        } as FormEntity
        // entities
      );
    } catch {
      // Soft-fail: exhibitor row + invitation already exist.
    }

    //defaults for user admin
    await createDefaultAvailabilities({
      entityId: user.id,
      entityModel: AvailabilitiesModel.USER,
      eventId: eventId,
    });
    await createDefaultNetworkingSettings(
      eventId,
      user.id,
      NetworkingModel.USER,
      user
    );
    const userFormTranslations = translations?.map((t) => ({
      name: user?.name || "",
      languageId: t?.languageId,
    }));

    try {
      await createParticipantFormTemplate(
        {
          whiteLabelId: whiteLabelId,
          eventId: eventId,
          createdById,
        },
        userFormTranslations as any,
        {
          json: {
            logoPosition: "right",
            pages: [
              {
                name: "page1",
                elements: [
                  {
                    type: "dropdown",
                    name: "question1",
                    title: "Meeting Location",
                    choices: [
                      {
                        value: "Item 1",
                        text: "Coffee Area",
                      },
                      {
                        value: "Item 2",
                        text: "Booth",
                      },
                      {
                        value: "Item 3",
                        text: "Networking Area",
                      },
                      {
                        value: "Item 4",
                        text: "Other",
                      },
                    ],
                  },
                  {
                    type: "text",
                    name: "question4",
                    visibleIf: "{question1} = 'Item 4'",
                    title: "Custom Location",
                  },
                  {
                    type: "dropdown",
                    name: "question2",
                    title: "Reason",
                    choices: [
                      {
                        value: "Item 1",
                        text: "Need a mentoring consultation",
                      },
                      {
                        value: "Item 2",
                        text: "Chat and idea exchange",
                      },
                    ],
                  },
                  {
                    type: "comment",
                    name: "question3",
                    title: "Description",
                  },
                ],
              },
            ],
          },
          numberOfQuestions: 4,
        } as any,
        {
          entityID: user?.id,
          type: FormEntityType.MEETING,
        } as FormEntity
      );
    } catch {
      // Soft-fail: exhibitor row + invitation already exist.
    }
    // create task entities for the exhibitor
    if (participantGroupId) {
      const participantGroup = await prisma.participantGroup.findUnique({
        where: {
          id: participantGroupId,
        },
        include: {
          tasks: true,
        },
      });
      await Promise.all(
        (participantGroup?.tasks || []).map(async (task) => {
          if (!task?.taskId) {
            console.error(`Task ID is missing for task:`, task);
            return;
          }

          return await prisma.taskEntity.create({
            data: {
              task: {
                connect: {
                  id: task.taskId,
                },
              },
              exhibitor: {
                connect: {
                  id: response.id,
                },
              },
            },
          });
        })
      );
      //defaults for exhibitor company
      await createDefaultAvailabilities({
        entityId: response.id,
        entityModel: AvailabilitiesModel.EXHIBITOR,
        eventId: eventId,
      });
      await createDefaultNetworkingSettings(
        eventId,
        response.id,
        NetworkingModel.EXHIBITOR,
        user,
        participantGroupId
      );
      const FormTranslations = translations?.map((t) => ({
        name: t?.name || "",
        languageId: t?.languageId,
      }));

      try {
        await createParticipantFormTemplate(
          {
            whiteLabelId: whiteLabelId,
            eventId: eventId,
            createdById,
          },
          FormTranslations as any,
          {
            json: {
              logoPosition: "right",
              pages: [
                {
                  name: "page1",
                  elements: [
                    {
                      type: "dropdown",
                      name: "question1",
                      title: "Meeting Location",
                      choices: [
                        {
                          value: "Item 1",
                          text: "Coffee Area",
                        },
                        {
                          value: "Item 2",
                          text: "Booth",
                        },
                        {
                          value: "Item 3",
                          text: "Networking Area",
                        },
                        {
                          value: "Item 4",
                          text: "Other",
                        },
                      ],
                    },
                    {
                      type: "text",
                      name: "question4",
                      visibleIf: "{question1} = 'Item 4'",
                      title: "Custom Location",
                    },
                    {
                      type: "dropdown",
                      name: "question2",
                      title: "Reason",
                      choices: [
                        {
                          value: "Item 1",
                          text: "Need a mentoring consultation",
                        },
                        {
                          value: "Item 2",
                          text: "Chat and idea exchange",
                        },
                      ],
                    },
                    {
                      type: "comment",
                      name: "question3",
                      title: "Description",
                    },
                  ],
                },
              ],
            },
            numberOfQuestions: 4,
          } as any,
          {
            entityID: response?.id,
            type: FormEntityType.MEETING,
          } as FormEntity
          // entities
        );
      } catch {
        // Soft-fail: exhibitor row + invitation already exist.
      }

      //defaults for user admin
      await createDefaultAvailabilities({
        entityId: user.id,
        entityModel: AvailabilitiesModel.USER,
        eventId: eventId,
      });
      await createDefaultNetworkingSettings(
        eventId,
        user.id,
        NetworkingModel.USER,
        user,
        participantGroupId
      );
      const userFormTranslations = translations?.map((t) => ({
        name: user?.name || "",
        languageId: t?.languageId,
      }));

      try {
        await createParticipantFormTemplate(
          {
            whiteLabelId: whiteLabelId,
            eventId: eventId,
            createdById,
          },
          userFormTranslations as any,
          {
            json: {
              logoPosition: "right",
              pages: [
                {
                  name: "page1",
                  elements: [
                    {
                      type: "dropdown",
                      name: "question1",
                      title: "Meeting Location",
                      choices: [
                        {
                          value: "Item 1",
                          text: "Coffee Area",
                        },
                        {
                          value: "Item 2",
                          text: "Booth",
                        },
                        {
                          value: "Item 3",
                          text: "Networking Area",
                        },
                        {
                          value: "Item 4",
                          text: "Other",
                        },
                      ],
                    },
                    {
                      type: "text",
                      name: "question4",
                      visibleIf: "{question1} = 'Item 4'",
                      title: "Custom Location",
                    },
                    {
                      type: "dropdown",
                      name: "question2",
                      title: "Reason",
                      choices: [
                        {
                          value: "Item 1",
                          text: "Need a mentoring consultation",
                        },
                        {
                          value: "Item 2",
                          text: "Chat and idea exchange",
                        },
                      ],
                    },
                    {
                      type: "comment",
                      name: "question3",
                      title: "Description",
                    },
                  ],
                },
              ],
            },
            numberOfQuestions: 4,
          } as any,
          {
            entityID: user?.id,
            type: FormEntityType.MEETING,
          } as FormEntity
        );
      } catch {
        // Soft-fail: exhibitor row + invitation already exist.
      }
    }

    return { ...response, token };
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

// get all exipiters that do not have a task entities with  task transition or the last task transition is new
export const getExhibitorsWithNoSubmittedTasks = async () => {
  const exhibitors = await prisma.exhibitor.findMany({
    where: {
      taskEntities: {
        some: {
          OR: [
            {
              taskTransitions: {
                none: {},
              },
            },
            {
              taskTransitions: {
                some: {
                  toState: TaskTransitionType.NEW,
                },
              },
            },
          ],
        },
      },
    },
    include: {
      event: {
        include: {
          translations: true,
          whiteLabel: true,
        },
      },
      taskEntities: {
        include: {
          taskTransitions: true,
          task: true,
        },
      },
    },
  });
  return exhibitors.map((exhibitor) => ({
    ...exhibitor,
    numberOfUncompletedTasks: exhibitor.taskEntities.filter(
      (taskEntity) =>
        taskEntity.taskTransitions.length === 0 ||
        taskEntity.taskTransitions[taskEntity.taskTransitions.length - 1]
          .toState === TaskTransitionType.NEW
    ).length,
  }));
};

// UPDATE
export const updateExhibitor = async (
  id: string,
  data: Prisma.ExhibitorCreateInput & {
    contactName?: string;
    contactJobTitle?: string;
    phone?: string;
    sector?: string | null;
    country?: string | null;
    company?: string | null;
  },
  translitions: Prisma.ExhibitorTranslationsCreateManyExhibitorInput[],

  participantGroupId?: string
) => {
  try {
    const participantGroup = participantGroupId
      ? await prisma.participantGroup.findUnique({
          where: {
            id: participantGroupId,
          },
          include: {
            tasks: true,
          },
        })
      : null;
    //   is data.email different from the old email
    let isNewEmail = false;
    const exhibitor = await prisma.exhibitor.findUnique({
      where: {
        id,
      },
      select: {
        id: true,
        email: true,
        eventId: true,
        participantGroupId: true,
        teamIds: true,
        event: { select: { whiteLabelId: true } },
      },
    });

    if (data.email !== exhibitor?.email) {
      isNewEmail = true;
      const exhibitorEmailExist = await prisma.exhibitor.findFirst({
        where: {
          email: data.email,
          eventId: exhibitor?.eventId,
        },
      });
      if (exhibitorEmailExist) {
        return {
          error: "Exhibitor email is already exist in the same event",
        };
      }
      const contactEmailConflict = await getCompanyAdminEmailConflict({
        teamIds: exhibitor?.teamIds ?? [],
        whiteLabelId: exhibitor?.event?.whiteLabelId,
        previousEmail: exhibitor?.email,
        email: typeof data.email === "string" ? data.email : null,
      });
      if (contactEmailConflict) {
        return { error: contactEmailConflict };
      }
    }
    const isOldParticipantGroup =
      (participantGroupId ?? null) === (exhibitor?.participantGroupId ?? null);
    // check if exhibitor name is exist in the same event
    const exhibitorName = translitions[0]?.name?.trim() ?? "";
    const exhibitorNameExist = exhibitorName
      ? await prisma.exhibitor.findFirst({
          where: {
            eventId: exhibitor?.eventId,
            translations: {
              some: {
                name: {
                  equals: exhibitorName,
                  mode: "insensitive",
                },
              },
            },
            NOT: {
              id,
            },
          },
        })
      : null;
    if (exhibitorNameExist) {
      return {
        error: "Exhibitor name is already exist in the same event",
      };
    }
    const {
      contactName,
      contactJobTitle,
      phone,
      sector,
      country,
      company,
      ...rest
    } = data;
    // Contact person only — never fall back to company `translitions.name`.
    const resolvedContactName =
      [contactName, translitions?.[0]?.contactName]
        .find((value) => typeof value === "string" && value.trim())
        ?.trim() || "";
    const response = await prisma.exhibitor.update({
      where: {
        id,
      },
      data: {
        ...rest,
        phone,
        participantGroup: participantGroupId
          ? { connect: { id: participantGroupId } }
          : { disconnect: true },
        taskEntities: !isOldParticipantGroup
          ? {
              deleteMany: {},
            }
          : {},
        ...(translitions.length > 0
          ? {
              translations: {
                deleteMany: {},
                createMany: {
                  data: translitions,
                },
              },
            }
          : {}),
        // Drop the search blob so the next company search rebuilds it from
        // the saved name and email. boothName is still not searched.
        searchCards: [],
      },
    });

    await syncCompanyAdminUserAndProfile({
      eventId: response.eventId,
      teamIds: exhibitor?.teamIds ?? [],
      whiteLabelId: exhibitor?.event?.whiteLabelId,
      previousEmail: exhibitor?.email,
      email: response.email,
      name: resolvedContactName || undefined,
      phone,
      jobTitle: contactJobTitle,
      sector,
      country,
      company,
      socialMedia: {
        website: response.website,
        facebook: response.facebook,
        linkedin: response.linkedin,
        twitter: response.x,
        instagram: response.instagram,
      },
    });

    // Meeting emails + magic links look up ParticipantInvitation by entityId+email.
    // Re-sign JWT so payload.email matches the new address (updateMany alone leaves stale claims).
    if (isNewEmail && exhibitor?.email && response.email) {
      const email = response.email;
      const invitations = await prisma.participantInvitation.findMany({
        where: {
          entityId: id,
          email: exhibitor.email,
          entityType: ParticipantType.EXHIBITOR,
        },
      });
      const secret = process.env.SECRET_HASH_PASSWORD as string;
      await Promise.all(
        invitations.map((invitation) => {
          const token = jwt.sign(
            {
              entityId: id,
              email,
              entityType: ParticipantType.EXHIBITOR,
            },
            secret
          );
          return prisma.participantInvitation.update({
            where: { id: invitation.id },
            data: { email, token },
          });
        })
      );
    }
    // create task entities for the exhibitor
    (participantGroup?.tasks || []).forEach(async (task) => {
      if (!task.taskId) {
        console.error(`Task ID is missing for task:`, task);
        return;
      }

      return await prisma.taskEntity.upsert({
        where: {
          taskId_entityID: {
            taskId: task.taskId,
            entityID: response.id,
          },
        },
        update: {
          task: {
            connect: {
              id: task.taskId,
            },
          },
          exhibitor: {
            connect: {
              id: id,
            },
          },
        },
        create: {
          task: {
            connect: {
              id: task.taskId,
            },
          },
          exhibitor: {
            connect: {
              id: id,
            },
          },
        },
      });
    });

    return {
      ...response,
      isNewEmail,
    };
  } catch (error: any) {
    console.log("error", error);
    if (error.code == "P2014") {
      throw new Error(
        "Unable to update exhibitor because it would violate the submitted tasks."
      );
    } else {
      throw new Error("An unexpected error occurred while updating exhibitor.");
    }
  }
};

export const upsertExhibitorsInvitationState = async (
  exhibitor: {
    id: string;
    email: string;
  },
  status: InvitationStatus
) => {
  try {
    const token = jwt.sign(
      {
        entityId: exhibitor.id,
        email: exhibitor.email,
        entityType: ParticipantGroupType.EXHIBITORS,
      },
      process.env.SECRET_HASH_PASSWORD as string
    );
    const response = await prisma.participantInvitation.upsert({
      where: {
        entityId_entityType_email: {
          entityId: exhibitor?.id,
          email: exhibitor.email,
          entityType: ParticipantType.EXHIBITOR,
        },
      },
      update: {
        status: status,
      },
      create: {
        email: exhibitor.email,
        status: status,
        entityId: exhibitor.id,
        entityType: ParticipantType.EXHIBITOR,
        token,
      },
    });

    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const updateExhibitorsInvitation = async (
  exhibtors: {
    id: string;
    email: string;
  }[],
  status: InvitationStatus
) => {
  try {
    const response = await prisma.participantInvitation.createMany({
      data: exhibtors.map((exhibitor) => {
        const token = jwt.sign(
          {
            entityId: exhibitor.id,
            email: exhibitor.email,
            entityType: ParticipantType.EXHIBITOR,
          },
          process.env.SECRET_HASH_PASSWORD as string
        );
        return {
          email: exhibitor.email,
          status: status,
          entityId: exhibitor.id,
          entityType: ParticipantType.EXHIBITOR,
          token,
          isOwner: true,
        };
      }),
    });

    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};
// DELETE
export const deleteExhibitor = async (id: string) => {
  try {
    const exhibitor = await prisma.exhibitor.findUnique({
      where: { id },
      select: {
        id: true,
        email: true,
        eventId: true,
        teamIds: true,
        participantGroupId: true,
        event: { select: { whiteLabelId: true } },
      },
    });
    if (!exhibitor) return true;

    await prisma.$transaction(
      async (tx) => {
        const taskEntitiesPromise = tx.taskEntity.findMany({
          where: { entityID: id },
          select: { id: true },
        });

        await cleanupCompanyTeamUsers(tx, {
          teamUserIds: exhibitor.teamIds,
          eventId: exhibitor.eventId,
          participantGroupId: exhibitor.participantGroupId,
          companyEntityId: id,
          entityType: UserRoleAssignmentEntityType.EXHIBITOR,
        });

        const taskEntities = await taskEntitiesPromise;
        const taskIds = taskEntities.map((t) => t.id);

        await Promise.all([
          tx.assignmentModel.deleteMany({
            where: {
              entityId: id,
              entityType: UserRoleAssignmentEntityType.EXHIBITOR,
            },
          }),
          tx.participantInvitation.deleteMany({
            where: { entityId: id, entityType: ParticipantType.EXHIBITOR },
          }),
          tx.formEntity.deleteMany({ where: { entityID: id } }),
          tx.networking.deleteMany({
            where: { entityId: id, entityModel: NetworkingModel.EXHIBITOR },
          }),
          tx.availability.deleteMany({
            where: { entityId: id, entityModel: AvailabilitiesModel.EXHIBITOR },
          }),
          tx.meetingParticipant.deleteMany({ where: { entityId: id } }),
          tx.message.deleteMany({ where: { senderId: id } }),
          tx.bookmark.deleteMany({ where: { entityId: id } }),
          tx.orderItem.updateMany({
            where: { exhibitorId: id },
            data: { exhibitorId: null },
          }),
          tx.entity.updateMany({
            where: { exhibitorId: id },
            data: { exhibitorId: null },
          }),
          tx.contactBookGroup.updateMany({
            where: { exhibitorId: id },
            data: { exhibitorId: null },
          }),
        ]);

        const connectionRequests = await tx.connectionRequest.findMany({
          where: {
            eventId: exhibitor.eventId,
            OR: [{ senderId: id }, { receiverId: id }],
          },
          select: { id: true },
        });
        const connectionRequestIds = connectionRequests.map((r) => r.id);
        if (connectionRequestIds.length) {
          const notifications = await tx.notification.findMany({
            where: { entityId: { in: connectionRequestIds } },
            select: { id: true },
          });
          const notificationIds = notifications.map((n) => n.id);
          await Promise.all([
            notificationIds.length
              ? tx.notificationTranslations.deleteMany({
                  where: { notificationId: { in: notificationIds } },
                })
              : Promise.resolve(),
            notificationIds.length
              ? tx.notification.deleteMany({
                  where: { id: { in: notificationIds } },
                })
              : Promise.resolve(),
            tx.connectionRequest.deleteMany({
              where: { id: { in: connectionRequestIds } },
            }),
          ]);
        }

        if (taskIds.length) {
          await Promise.all([
            tx.formResultEntity.deleteMany({
              where: { taskId: { in: taskIds } },
            }),
            tx.taskTransition.deleteMany({
              where: { taskEntityId: { in: taskIds } },
            }),
            tx.taskEntity.deleteMany({ where: { entityID: id } }),
          ]);
        }

        await tx.exhibitor.update({
          where: { id },
          data: { team: { set: [] } },
        });
        await tx.exhibitor.delete({ where: { id } });
      },
      { timeout: 60_000, maxWait: 10_000 }
    );

    await invalidateEventPageCacheForEvent(exhibitor.eventId);

    return true;
  } catch (error: any) {
    throw new Error(error?.message);
  }
};

export const addExhibitorTeamMember = async (
  exhibitorId: string,
  data: Prisma.UserCreateInput & {
    eventId: string;
    jobTitle?: string;
    qrReferenceNumber?: string;
    profileId?: string;
    permissions?: Prisma.ExhibitorPermissionsCreateInput;
    participantImportId?: string;
  }
) => {
  try {
    const contactBookGroup = await getContactBookGroupByExhibitorId(
      exhibitorId
    );
    const token = jwt.sign(
      {
        entityId: exhibitorId,
        email: data.email as string,
        entityType: ParticipantGroupType.EXHIBITORS,
      },
      process.env.SECRET_HASH_PASSWORD as string
    );
    // Only keep scalar user fields here so Prisma doesn't infer
    // the unchecked input variant from arbitrary nested properties on `data`.
    const {
      eventId,
      jobTitle,
      qrReferenceNumber,
      profileId,
      permissions,
      participantImportId,
      name,
      email,
      phoneNumber,
    } = data;
    const whiteLabel = await getWhiteLabelByEventId(eventId);
    const accessibleWhiteLabelId = whiteLabel?.id as string;
    const normalizedEmail = String(email).trim().toLowerCase();
    const [existingProfile, existingInvitation] = await Promise.all([
      prisma.profile.findFirst({
        where: {
          eventId,
          OR: [
            { email: normalizedEmail },
            { user: { email: normalizedEmail } },
          ],
        },
        select: { id: true },
      }),
      prisma.participantInvitation.findFirst({
        where: {
          entityId: exhibitorId,
          email: normalizedEmail,
        },
        select: { id: true },
      }),
    ]);
    if (existingProfile || existingInvitation) {
      throw new Error(
        `Email "${normalizedEmail}" already belongs to a participant in this event`
      );
    }
    await adoptUnscopedUser(normalizedEmail, accessibleWhiteLabelId);
    const response = await prisma.user.upsert({
      where: {
        email_accessibleWhiteLabelId: {
          email: normalizedEmail,
          accessibleWhiteLabelId,
        },
      },
      create: {
        name: (name as string) ?? "",
        email: normalizedEmail,
        phoneNumber: phoneNumber as string,
        accessibleWhiteLabelId,
        participantImportId,
        events: {
          connect: {
            id: eventId,
          },
        },
        profiles: {
          create: {
            event: { connect: { id: eventId } },
            jobTitle: jobTitle as string,
            qrReferenceNumber,
            name: (name as string) ?? "",
            email: normalizedEmail,
            phoneNumber: phoneNumber as string,
            contactBookGroup: { connect: { id: contactBookGroup.id } },
            exhibitorPermissions: permissions ?? undefined,
            participantImportId,
          },
        },
        exhibitors: {
          connect: {
            id: exhibitorId,
          },
        },
        userRoleAssignments: {
          create: {
            entityType: UserRoleAssignmentEntityType.EXHIBITOR,
            entityId: exhibitorId,
          },
        },
      },
      update: {
        accessibleWhiteLabelId,
        name: (name as string) ?? "",
        email: normalizedEmail,
        phoneNumber: phoneNumber as string,
        exhibitors: {
          connect: {
            id: exhibitorId,
          },
        },
        profiles: profileId
          ? {
              upsert: {
                where: {
                  id: profileId,
                },
                create: {
                  eventId,
                  jobTitle: jobTitle as string,
                  qrReferenceNumber,
                  name: name ?? "",
                  email: normalizedEmail,
                  phoneNumber: phoneNumber as string,
                  contactBookGroupId: contactBookGroup.id,
                  exhibitorPermissions: permissions ?? undefined,
                  participantImportId,
                },
                update: {
                  eventId,
                  jobTitle: jobTitle as string,
                  qrReferenceNumber,
                  name: name ?? "",
                  email: normalizedEmail,
                  phoneNumber: phoneNumber as string,
                  contactBookGroupId: contactBookGroup.id,
                  exhibitorPermissions: permissions ?? undefined,
                  participantImportId,
                },
              },
            }
          : {
              create: {
                eventId,
                jobTitle: jobTitle as string,
                qrReferenceNumber,
                name: name ?? "",
                email: normalizedEmail,
                phoneNumber: phoneNumber as string,
                contactBookGroupId: contactBookGroup.id,
                exhibitorPermissions: permissions ?? undefined,
                participantImportId,
              },
            },
        userRoleAssignments: {
          create: {
            entityType: UserRoleAssignmentEntityType.EXHIBITOR,
            entityId: exhibitorId,
          },
        },
      },
    });

    await prisma.participantInvitation.upsert({
      where: {
        entityId_entityType_email: {
          entityId: exhibitorId,
          email: normalizedEmail,
          entityType: ParticipantType.EXHIBITOR,
        },
      },
      update: {
        status: InvitationStatus.PENDING,
        token,
        participantImportId,
      },
      create: {
        email: normalizedEmail,
        status: InvitationStatus.PENDING,
        entityId: exhibitorId,
        entityType: ParticipantType.EXHIBITOR,
        token,
        isOwner: false,
        participantImportId,
      },
    });

    return { ...response, token };
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const removeExhibitorTeamMember = async (
  exhibitorId: string,
  userId: string
) => {
  try {
    await assertNotOwnerTeamMember(
      exhibitorId,
      ParticipantType.EXHIBITOR,
      userId
    );

    const response = await prisma.exhibitor.update({
      where: {
        id: exhibitorId,
      },
      data: {
        team: {
          disconnect: {
            id: userId,
          },
        },
        assignmentModels: {
          deleteMany: {
            userId: userId,
            entityType: UserRoleAssignmentEntityType.EXHIBITOR,
            entityId: exhibitorId,
          },
        },
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const mapParticipantGroupTypeToParticipantType = (
  participantGroupType: ParticipantGroupType
) => {
  switch (participantGroupType) {
    case ParticipantGroupType.EXHIBITORS:
      return ParticipantType.EXHIBITOR;
    case ParticipantGroupType.SPONSORS:
      return ParticipantType.SPONSOR;
    case ParticipantGroupType.VISITORS:
      return ParticipantType.VISITOR;
    case ParticipantGroupType.MEDIA:
      return ParticipantType.MEDIA;
    case ParticipantGroupType.SPEAKERS:
      return ParticipantType.SPEAKER;
    case ParticipantGroupType.SUPPORT:
      return ParticipantType.SUPPORT;
    default:
      return ParticipantType.VISITOR;
  }
};

export const updateParticipantInvitationStatus = async (
  entityId: string,
  email: string,
  entityType: ParticipantGroupType
) => {
  const token = jwt.sign(
    { entityId: entityId, email: email, entityType: entityType },
    process.env.SECRET_HASH_PASSWORD as string
  );
  try {
    const response = await prisma.participantInvitation.upsert({
      where: {
        entityId_entityType_email: {
          entityId: entityId,
          email: email,
          entityType: mapParticipantGroupTypeToParticipantType(entityType),
        },
      },
      create: {
        entityId: entityId,
        entityType: mapParticipantGroupTypeToParticipantType(entityType),
        status: InvitationStatus.ACCEPTED,
        email: email,
        token: token,
      },
      update: {
        status: InvitationStatus.ACCEPTED,
      },
    });
    return response;
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

export const updateExhibitorSendAtTime = async (id: string, sendAt: Date) => {
  try {
    const response = await prisma.exhibitor.update({
      where: {
        id,
      },
      data: {
        lastReminderSentAt: sendAt,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const getExhibitorUploads = async (exhibitorId: string) => {
  const exhibitor = await prisma.exhibitor.findUnique({
    where: {
      id: exhibitorId,
    },
    include: {
      translations: {
        where: {
          language: {
            code: "en",
          },
        },
      },
      taskEntities: {
        include: {
          taskTransitions: true,
          formResults: {
            include: {
              formResult: true,
            },
          },
          task: {
            include: {
              taskTranslations: {
                where: {
                  language: {
                    code: "en",
                  },
                },
              },
              formEntity: {
                include: {
                  formTemplate: true,
                },
              },
            },
          },
        },
      },
    },
  });
  const uploads =
    exhibitor?.taskEntities?.map((taskEntity) => {
      return taskEntity.uploadedFiles?.map((file) => {
        return {
          url: file.url,
          fileName: `${file.key}`,
          key: file.key,
        };
      });
    }) || [];

  return {
    files: uploads.flat(),
    name: exhibitor?.translations?.[0]?.name,
    boothName: exhibitor?.boothName,

    tasks:
      exhibitor?.taskEntities?.map((taskEntity) => {
        return {
          name: taskEntity.task.taskTranslations?.[0]?.name,
          timeSubmitted:
            taskEntity.taskTransitions[taskEntity.taskTransitions.length - 1]
              ?.createdAt,
          files: taskEntity.uploadedFiles,
          type: taskEntity.task.type,
          results: taskEntity.formResults[0]?.formResult?.surveyResult
            ? transformSurveyResult(
                taskEntity.task.formEntity?.formTemplate?.json || {},
                taskEntity.formResults[0]?.formResult?.surveyResult
              )
            : undefined,
        };
      }) || [],
  };
};

export const getExhibitorsSubmitedData = async (eventId: string) => {
  const exhibitor = await prisma.exhibitor.findMany({
    where: {
      eventId: eventId,
    },
    include: {
      translations: {
        where: {
          language: {
            code: "en",
          },
        },
      },
      taskEntities: {
        include: {
          taskTransitions: true,
          formResults: {
            include: {
              formResult: true,
            },
          },
          task: {
            include: {
              taskTranslations: {
                where: {
                  language: {
                    code: "en",
                  },
                },
              },
              formEntity: {
                include: {
                  formTemplate: {
                    select: {
                      id: true,
                      json: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  });
  const data = exhibitor.map((exhibitor) => {
    const uploads =
      exhibitor?.taskEntities?.map((taskEntity) => {
        return taskEntity.uploadedFiles?.map((file) => {
          return {
            url: file.url,
            fileName: `${file.key}`,
            key: file.key,
          };
        });
      }) || [];

    return {
      files: uploads.flat(),
      name: exhibitor?.translations?.[0]?.name,
      boothName: exhibitor?.boothName,
      tasks:
        exhibitor?.taskEntities?.map((taskEntity) => {
          return {
            name: taskEntity.task.taskTranslations?.[0]?.name,
            files: taskEntity.uploadedFiles,
            type: taskEntity.task.type,
            timeSubmitted:
              taskEntity.taskTransitions[taskEntity.taskTransitions.length - 1]
                ?.createdAt,
            results: taskEntity.formResults?.[0]?.formResult?.surveyResult
              ? transformSurveyResult(
                  taskEntity.task.formEntity?.formTemplate?.json || {},
                  taskEntity.formResults?.[0]?.formResult?.surveyResult
                )
              : undefined,
          };
        }) || [],
    };
  });
  return data;
};

export const exportExhibitorsList = async (eventId: string, lang?: string) => {
  try {
    // Fetch exhibitors from the database
    const exhibitors = (await prisma.exhibitor.findMany({
      // @ts-ignore
      where: {
        teamIds: { isEmpty: false },
        eventId,
      },
      include: {
        participantGroup: {
          include: {
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        taskEntities: {
          include: {
            formResults: {
              include: {
                formResult: true,
              },
            },
            taskTransitions: true,
            task: {
              include: {
                formEntity: {
                  include: {
                    formTemplate: true,
                  },
                },
                taskTranslations: {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                },
              },
            },
          },
        },
        exhibitorInvitations: true,
        translations: lang
          ? {
              where: {
                language: {
                  code: lang,
                },
              },
            }
          : true,
      },
      orderBy: {
        createdAt: "desc", // Order by creation date or any other field
      },
    })) as any;

    // Return the data table with pagination info
    return {
      result: exhibitors.map((exhibitor: any) => {
        const groupName =
          exhibitor.participantGroup?.translations?.find(
            (item: any) => item.language?.code === lang
          )?.name ?? exhibitor.participantGroup?.translations?.[0]?.name;

        return {
          ...exhibitor,
          name: exhibitor.translations?.[0]?.name,
          bio: exhibitor.translations?.[0]?.bio,
          participantGroup: exhibitor.participantGroup
            ? { ...exhibitor.participantGroup, name: groupName }
            : null,

          completedTasksPercentage: {
            total: exhibitor.taskEntities?.length || 0,
            completed:
              exhibitor.taskEntities?.filter(
                (task: any) =>
                  task.taskTransitions[task.taskTransitions.length - 1]
                    ?.toState === TaskTransitionType.COMPLETED ||
                  task.taskTransitions[task.taskTransitions.length - 1]
                    ?.toState === TaskTransitionType.SUBMITTED
              )?.length || 0,
          },
          taskEntity: exhibitor.taskEntities?.map((task: any) => {
            return {
              ...task,
              formResult: task.formResults[0]?.formResult
                ? {
                    ...task.formResults[0].formResult,
                    surveyResult: transformSurveyResult(
                      task.task.formEntity.formTemplate.json,
                      task.formResults[0].formResult.surveyResult
                    ),
                  }
                : null,
              task: {
                ...task.task,
                name: task.task.taskTranslations?.[0]?.name,
              },
            };
          }),
          hasUploadedFiles: exhibitor.taskEntities?.some(
            (task: any) =>
              task.uploadedFiles.length > 0 || task.formResults[0]?.formResult
          ),
        };
      }),
    };
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

export const updateTeamMember = async (
  id: string,
  updatedData: Prisma.UserUpdateInput & {
    eventId: string;
    jobTitle?: string;
    profileId: string;
    permissions?: Prisma.ExhibitorPermissionsCreateInput;
  },
  eventId: string,
  exhibitorId: string
) => {
  const contactBookGroup = await getContactBookGroupByExhibitorId(exhibitorId);
  return updatePortalTeamMember({
    userId: id,
    entityId: exhibitorId,
    entityType: ParticipantType.EXHIBITOR,
    eventId,
    contactBookGroupId: contactBookGroup.id,
    updatedData: {
      name: updatedData.name as string | undefined,
      email: updatedData.email as string | undefined,
      jobTitle: updatedData.jobTitle,
      phoneNumber: updatedData.phoneNumber as string | undefined,
      profileId: updatedData.profileId,
      permissions: updatedData.permissions,
    },
  });
};

export const updateExhibitorSetting = async (
  data: Prisma.ExhibitorSettingsCreateInput & { eventId: string }
) => {
  if (data.id) {
    return await prisma.exhibitorSettings.update({
      where: { id: data.id },
      data: {
        supportEmail: data.supportEmail,
        dashboardLogo: data.dashboardLogo,
        welcomeDescription: data.welcomeDescription,
      },
    });
  }
  return await prisma.exhibitorSettings.create({
    data: {
      supportEmail: data.supportEmail,
      dashboardLogo: data.dashboardLogo,
      welcomeDescription: data.welcomeDescription,
      eventId: data.eventId,
    },
  });
};

export const getExhibitorSetting = async (eventId: string) => {
  return await prisma.exhibitorSettings.findUnique({
    where: { eventId },
  });
};

export const countOrganizationExhibitors = async (organizationId: string) => {
  return await prisma.exhibitor.count({
    where: {
      event: {
        organizationIds: { has: organizationId },
      },
    },
  });
};
